import { Component, NgZone, OnDestroy, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { Subscription, forkJoin } from 'rxjs';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';

import { MenuCommonComponent } from '@app/shared/menu-common/menu-common.component';
import { HrService, hrErrorMessage } from '../hr.service';
import { CommonService } from '@app/common/common.service';
import { ValuesService } from '@app/common/values/values.service';
import { LoginService } from '@app/common/login.service';
import { AlertService } from '@app/shared/alert.service';
import { OverrideDialogComponent, OverrideDialogData, OverrideDialogResult, OverridePlace } from './override-dialog/override-dialog.component';
import { DayCloseDialogComponent, DayCloseDialogData } from './day-close-dialog/day-close-dialog.component';
import Swal from 'sweetalert2';
import { HrAbsence, HrActivity, HrEmployee, HrLock, HrNote, HrOverride, HrPublication, HrSchedule, HrTeam, hrActivityColor, hrEmployeeColor, hrEmployeeNameOf, hrIsHrManager, hrOpeningOf, hrPeriodLabel, hrResolveActivities, hrScheduleActivityId } from '@app/shared/_models/hr';
import { C_value, Store, User } from '@app/shared/_models/common';
import {
	C_VALUES_HR_ABSENCE_TYPE, C_VALUES_HR_ROSTER, C_VALUES_HR_TEAMS, EVENTTYP_DELETE, EVENTTYP_INSERT,
	EVENTTYP_HR_OVERRIDE_DAY, EVENTTYP_UPDATE, HR_ABSENCE_STATE_APPROVED,
	HR_ABSENCE_STATE_REQUESTED, HR_GRID_END_MIN, HR_GRID_SNAP_MIN, HR_GRID_START_MIN, HR_OVERRIDE_ADD,
	HR_OVERRIDE_REMOVE, HR_PERSON_COLORS, OBJECTTYP_HR_DAY_VALIDATION, OBJECTTYP_HR_NOTE, OBJECTTYP_HR_OVERRIDE,
	OBJECTTYP_HR_PUBLICATION,
} from '@app/shared/constants';
import {
	EffectiveSlot, LegalIssue, addDays, crossWeekRestIssues, deltaLabel, effectiveSlotsForRange, frenchHolidaysOf, hoursLabel,
	isoWeekOf, isoWeekdayOf, legalIssuesForWeek, minutesLabel, mondayOf, plannedMinutes,
	scheduleAppliesOn, weekIndexOf,
} from '../hr-schedule-engine';
import { HR_DAY_LABELS } from '../schedule/schedule.component';
import { HrTimeSource, PayrollRules, hrPayrollRulesOf } from '../hr-payroll';
import { effectiveWorkRules, employeeWorkProfileOf, weekOutcome } from '../hr-worktime';
import { hrCountedLeaveDays } from '../hr-leave';
import { closedHolidaysInRange, holidayMapOf } from '../hr-holidays';
import { DayCloseRow } from '../hr-validation';
import { pairClocks } from '../hr-timesheet';
import { PrintDoc } from '../hr-print';
import { HrPrintService, hrPrintErrorMessage } from '../hr-print.service';
import { buildPlanningPrintDoc, buildSchedulesPrintDoc } from './hr-planning-print';
import { HrTextOnPipe } from '../hr-text-on.pipe';
import { rosterMoveVisible, rosterOrderOf, rosterUniverse, withRosterSeq } from '../hr-roster';
import { DragBandVM, EMPTY_DRAG_BAND, dragBandOfRow } from '../hr-drag-band';
import { AvailabilityMark, availabilityBadgeOf, availabilityMarksForRange, conflictsWithUnavailability } from '../hr-availability';
import { buildPublicationDigest, publicationDiff, publicationSummary } from '../hr-publication';
import {
	TimelineBar, TimelineMarker, TimelineWindow, barGeometry, mergeRanges, openingBands,
	openingMarkers, timelineTicks, timelineWindow,
} from '../hr-timeline';
import { todayStr } from '@app/shared/time';

interface AbsenceBadge { label: string; color: string; pending: boolean; }

// Le `data` de l'élément du référentiel c_values 30 (ordre d'apparition des salariés) : l'ordre
// vit dans `member_seq` — un rang par id de salarié, clés JSON donc chaînes (hr-roster.ts). Toute
// autre clé appartient à d'autres écrans et se conserve telle quelle (withRosterSeq).
interface RosterData {
	member_seq?: Record<string, number>;
	[extra: string]: unknown;
}

interface PlanningCell {
	dat: string;
	slots: EffectiveSlot[];
	absence: AbsenceBadge | null;
	overrides: HrOverride[];		// exceptions existantes de l'employé ce jour (tous EDT)
}

interface PlanningRow {
	id_employee: number;
	name: string;
	cells: PlanningCell[];			// 7 jours
	total_min: number;					// heures planifiées de la semaine (tous EDT confondus, pauses déduites)
	// Les heures de cette personne se comptent-elles ? Faux pour un cadre dirigeant, un gérant non
	// salarié ou un forfait jours : la ligne reste planifiée, mais aucun total ne la concerne.
	counts_hours: boolean;
	assimilated_min: number;		// heures de congés payés ASSIMILÉES à du travail dans l'assiette
	assimilated_days: number;		// jours de congés décomptés de la semaine (« 3 j de CP »)
	holiday_days: number;				// jours fériés CHÔMÉS de la semaine (heures maintenues)
	holiday_min: number;
	justified_days: number;			// jours d'absence justifiée comptés comme du travail
	unjustified_days: number;		// jours d'absence non assimilée (sans solde, injustifiée)
	basis_min: number;					// assiette légale = travaillé + assimilé
	contract_min: number | null;	// volume contractuel hebdo, null si non renseigné
	delta_min: number | null;		// assiette − contrat : + heures majorées, − heures manquantes
	extra_min: number;					// heures au-delà du seuil (supplémentaires ou complémentaires)
	extra_kind: 'overtime' | 'complementary';
	issues: LegalIssue[];
}

// Vue par POSTE (P04) : une ligne par service, les personnes dans les cellules.
interface PosteCell {
	dat: string;
	entries: { slot: EffectiveSlot; name: string }[];
	people: number;			// personnes DISTINCTES (une coupure méridienne ne fait pas deux présents)
	minutes: number;
}

interface PosteRow {
	id_activity: number;
	name: string;
	color: string;
	seq: number;				// ordre d'affichage du service (le même que l'écran Organisation)
	cells: PosteCell[];	// 7 jours
	total_min: number;
}

interface ScheduleSection {
	schedule: HrSchedule;
	activityName: string;
	cycleLabel: string;					// « Semaine 2/2 du cycle » (vide si cycle 1)
	rows: PlanningRow[];
}

// Vue par SALARIÉ : une ligne par personne, TOUS ses créneaux de la semaine (tous services et
// magasins) sur la même ligne. Répond à « où travaille-t-elle à chaque instant ? ».
interface EmployeeCell {
	dat: string;
	slots: EffectiveSlot[];			// tous services/magasins confondus, ce jour
	absence: AbsenceBadge | null;
}

interface EmployeeRow {
	id_employee: number;
	name: string;
	cells: EmployeeCell[];			// 7 jours
	total_min: number;
	counts_hours: boolean;			// cf. PlanningRow — sans décompte, la ligne n'affiche aucun total
	assimilated_min: number;
	assimilated_days: number;
	holiday_days: number;
	holiday_min: number;
	justified_days: number;
	unjustified_days: number;
	basis_min: number;
	contract_min: number | null;
	delta_min: number | null;
	extra_min: number;
	extra_kind: 'overtime' | 'complementary';
	issues: LegalIssue[];
}

// Glisser en cours sur une barre du planning (déplacement du corps ou tirage d'un bord).
// La géométrie de la piste est FIGÉE au pointerdown : la barre ne saute pas sous le pointeur.
interface PlanningDragState {
	slot: EffectiveSlot;
	mode: 'move' | 'start' | 'end';
	rect: DOMRect;
	originMin: number;
	before: { start_min: number; end_min: number };
	moved: boolean;
}

// Planning RÉEL d'une semaine calendaire par magasin — /hr/planning(/:date).
// Projette les emplois du temps (rotation résolue), superpose absences, exceptions et fériés.
// Clic sur une case = exception ponctuelle (remplacement, annulation de journée, renfort).
// La date vit dans l'URL (F5-safe, comme le board des tournées).
@Component({
	selector: 'app-hr-planning',
	standalone: true,
	imports: [MenuCommonComponent, ReactiveFormsModule, RouterLink, DragDropModule, MatFormFieldModule, MatSelectModule,
		MatButtonModule, MatButtonToggleModule, MatSlideToggleModule, MatIconModule, MatTooltipModule, HrTextOnPipe],
	templateUrl: './hr-planning.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrl: './hr-planning.component.scss',
})
export class HrPlanningComponent implements OnInit, OnDestroy {

	public hrService = inject(HrService);
	private commonService = inject(CommonService);
	private valuesService = inject(ValuesService);
	private loginService = inject(LoginService);
	private alertService = inject(AlertService);
	private printService = inject(HrPrintService);
	private route = inject(ActivatedRoute);
	private router = inject(Router);
	private dialog = inject(MatDialog);
	private zone = inject(NgZone);

	// Le planning se LIT par toute l'équipe ; il ne se MODIFIE que par le responsable RH. Ce seul
	// booléen commande l'édition : barres déplaçables, clôture de journée, notes, publication.
	// (La barrière réelle est serveur : toute écriture RH est refusée en 403 sans le rôle.)
	public isHrManager = false;

	public date: string = todayStr();				// jour demandé (URL) — la semaine affichée est celle de ce jour
	public monday: string = mondayOf(todayStr());
	public weekDays: {
		dat: string; label: string; holiday: string | null; today: boolean; locked: boolean; note: string;
		// Amplitude d'ouverture du jour, « 09:00 – 19:00 » (vide si le magasin n'ouvre pas) : l'écran
		// la donne par la frise, le papier n'a que ce texte pour la porter.
		hours: string;
		// open : le magasin ouvre-t-il ce jour (au moins un service a un horaire) ; hasActivity : quelqu'un
		// est-il planifié / une note existe-t-elle. Un jour fermé ET vide est masqué par défaut.
		open: boolean; hasActivity: boolean;
		// Frise : repères d'ouverture/fermeture du jour et bandes de fond. Propres à CHAQUE jour
		// (le samedi n'ouvre pas comme le mardi), mais posés sur l'échelle commune de la grille.
		markers: TimelineMarker[];
		bands: TimelineBar[];
	}[] = [];

	// Échelle COMMUNE à toute la grille : sans elle, 9h du lundi ne serait pas aligné avec 9h
	// du mardi et la comparaison visuelle — le seul intérêt des barres — serait perdue.
	public timeWin: TimelineWindow = timelineWindow([], []);
	public ticks: { min: number; label: string; left_pct: number }[] = [];
	public weekLabel = '';
	public sections: ScheduleSection[] = [];
	// P04 — bascule Salarié ⇄ Poste sur les MÊMES données (EffectiveSlot).
	//   'employees' → une ligne par salarié : « où travaille-t-il à chaque instant ».
	//   'postes'    → une ligne par service : « qui tient ce poste, et combien ».
	public viewMode: 'employees' | 'postes' = 'employees';
	public posteRows: PosteRow[] = [];
	public employeeRows: EmployeeRow[] = [];
	// ORDRE D'APPARITION des salariés — rangé à la main, enregistré, et le même d'une semaine à
	// l'autre. L'alphabet ne dit rien d'une équipe : le gérant range ses lignes comme il range son
	// monde (les deux qui se relaient l'une sous l'autre, l'apprenti sous son tuteur, le renfort du
	// samedi en bas). Il vit dans le référentiel c_values 30 — le planning traverse les emplois du
	// temps, son ordre ne peut pas vivre dans l'un d'eux (`rosterData` = le `data` de l'élément).
	public rosterData: RosterData | null = null;
	// Le glissé d'une ligne : celle qu'on tient, et la bande qui suit la souris (hr-drag-band.ts).
	public movingId: number | null = null;
	public rowBand: DragBandVM = EMPTY_DRAG_BAND;
	public bandCells: { kind: 'name' | 'day' | 'total'; dayIdx: number; left: number; width: number }[] = [];
	// Salariés ajoutés à la main via la ligne « + salarié » (renfort, mobilité).
	public extraEmployeeIds = new Set<number>();
	public addEmpCtrl = new FormControl<number | null>(null);
	// Jours de fermeture du magasin masqués par défaut ; le toggle les réaffiche.
	public showClosedDays = false;
	// null = « Tous les magasins », et c'est le DÉFAUT : la question posée au planning est « qui
	// travaille cette semaine ? », elle ne s'arrête pas à un établissement. S'ouvrir sur le magasin
	// de la station cachait d'emblée la moitié de l'organisation — notamment les salariés mobiles,
	// qui n'apparaissaient que sur l'un de leurs lieux.
	public storeCtrl = new FormControl<number | null>(null);
	// O05 — filtre d'équipe (null = tout le monde).
	public teamCtrl = new FormControl<number | null>(null);
	public teams: HrTeam[] = [];
	public stores: Store[] = [];
	public loading = false;
	// Mode de décompte de l'établissement : décide ce que la clôture de journée propose.
	public source: HrTimeSource = 'planning';
	// Règles légales et conventionnelles de l'établissement (c_values 25) : seuils, majorations,
	// assiette des heures supplémentaires. Le planning en a besoin pour dire le vrai écart.
	private rules: PayrollRules = hrPayrollRulesOf(null);

	private schedules: HrSchedule[] = [];
	private activities: HrActivity[] = [];
	private employees: HrEmployee[] = [];
	private users: User[] = [];
	private absences: HrAbsence[] = [];
	private overrides: HrOverride[] = [];
	private absenceTypes: C_value[] = [];
	private locks: HrLock[] = [];
	private notes: HrNote[] = [];
	private availabilityMarks: AvailabilityMark[] = [];
	// Publication (L6) : la projection courante sert d'empreinte, la dernière publication de référence.
	private allEffective: EffectiveSlot[] = [];
	// La VEILLE de la semaine affichée (dimanche précédent), projetée pour le contrôle du repos
	// quotidien dimanche → lundi (L3131-1) — jamais affichée.
	private prevDayEffective: EffectiveSlot[] = [];
	private publications: HrPublication[] = [];

	private routeSub?: Subscription;
	private storeSub?: Subscription;
	private teamSub?: Subscription;
	private addEmpSub?: Subscription;

	// Glisser une barre : le corps déplace, les poignées redimensionnent. Le résultat est
	// enregistré dans le PLANNING (pas comme une exception : le planning est le réalisé).
	private drag: PlanningDragState | null = null;
	private onMove = (e: PointerEvent) => this.zone.run(() => this.dragMove(e));
	private onUp = (e: PointerEvent) => this.zone.run(() => this.dragEnd(e));

	ngOnInit(): void {
		// Changer de magasin remet les renforts à zéro : ils ont été ajoutés POUR le magasin affiché.
		this.storeSub = this.storeCtrl.valueChanges.subscribe(() => {
			this.extraEmployeeIds.clear();
			this.buildSections();
		});
		this.teamSub = this.teamCtrl.valueChanges.subscribe(() => this.buildSections());
		this.addEmpSub = this.addEmpCtrl.valueChanges.subscribe(id => this.onAddEmployee(id));
		this.routeSub = this.route.paramMap.subscribe(params => {
			const dat = params.get('date');
			this.loadWeek(dat && /^\d{4}-\d{2}-\d{2}$/.test(dat) ? dat : todayStr());
		});
	}

	ngOnDestroy(): void {
		this.routeSub?.unsubscribe();
		this.storeSub?.unsubscribe();
		this.teamSub?.unsubscribe();
		this.addEmpSub?.unsubscribe();
		this.unbindDrag();
	}

	// ============================================================
	// Chargement d'une semaine
	// ============================================================

	private loadWeek(dat: string): void {
		this.date = dat;
		this.monday = mondayOf(dat);
		const to = addDays(this.monday, 6);
		this.loading = true;

		const holidays = new Map([...frenchHolidaysOf(+this.monday.slice(0, 4)), ...frenchHolidaysOf(+to.slice(0, 4))]);
		this.weekDays = [0, 1, 2, 3, 4, 5, 6].map(i => {
			const d = addDays(this.monday, i);
			return {
				dat: d,
				label: HR_DAY_LABELS[i].slice(0, 3) + ' ' + d.slice(8, 10) + '/' + d.slice(5, 7),
				holiday: holidays.get(d) ?? null,
				today: d === todayStr(),
				locked: false,			// renseignés par refreshDayFlags() : dépendent du magasin choisi
				note: '',
				hours: '',				// renseigné par refreshTimeline()
				open: true,				// renseignés par refreshTimeline()/refreshDayActivity() (dépendent du magasin)
				hasActivity: true,
				markers: [],			// renseignés par refreshTimeline() : dépendent des services du magasin
				bands: [],
			};
		});
		this.weekLabel = 'S' + isoWeekOf(this.monday) + ' — du ' + this.fmtShort(this.monday) + ' au ' + this.fmtShort(to);

		forkJoin({
			schedules: this.hrService.getSchedules$(),
			activities: this.hrService.getActivities$(),
			employees: this.hrService.getEmployees$(),
			users: this.hrService.getUsers$(),
			stores: this.commonService.getStores(),
			// Un jour de plus en amont (le dimanche précédent) : c'est lui qui permet de contrôler le
			// repos quotidien dimanche → lundi (L3131-1), invisible d'une semaine isolée. Tous les
			// consommateurs filtrent par date : la veille ne pollue aucun affichage de la semaine.
			absences: this.hrService.getAbsences$(addDays(this.monday, -1), to),
			overrides: this.hrService.getOverrides$(addDays(this.monday, -1), to),
			absenceTypes: this.hrService.valuesOf$(C_VALUES_HR_ABSENCE_TYPE),
			locks: this.hrService.getLocks$(this.monday, to),
			notes: this.hrService.getNotes$(this.monday, to),
			availabilities: this.hrService.getAvailabilities$(),
			publications: this.hrService.getPublications$(this.monday, to),
			teams: this.hrService.valuesOf$(C_VALUES_HR_TEAMS),
			payrollRules: this.hrService.payrollRules$(),
			// Référentiel créé au premier déplacement de ligne : son absence est le cas NORMAL, pas
			// une panne — on retombe alors sur l'alphabet.
			roster: this.hrService.valuesOf$(C_VALUES_HR_ROSTER),
		}).subscribe(({ schedules, activities, employees, users, stores, absences, overrides, absenceTypes, locks, notes, availabilities, publications, teams, payrollRules, roster }) => {
			this.teams = teams.filter(t => t.is_active !== false).map(t => HrTeam.fromValue(t));
			this.rosterData = roster[0]?.data ?? null;
			this.rules = payrollRules.rules;
			this.source = this.rules.time_source;
			this.schedules = schedules;
			this.activities = hrResolveActivities(activities, schedules);
			this.employees = employees;
			this.users = users;
			// Le planning d'équipe se LIT par tout le monde — c'est un document d'organisation, il
			// est fait pour être affiché. Le MODIFIER (poser un créneau, clôturer une journée,
			// publier, annoter) reste au responsable RH.
			this.isHrManager = hrIsHrManager(this.loginService.loggeduser, users);
			this.stores = stores;
			this.absences = absences;
			this.overrides = overrides;
			this.absenceTypes = absenceTypes;
			this.locks = locks;
			this.notes = notes;
			this.publications = publications;
			// Les récurrences n'ont pas de date : on les projette sur la semaine affichée.
			this.availabilityMarks = availabilityMarksForRange(
				availabilities, this.monday, to, this.weekDays.map(d => d.dat));
			this.loading = false;
			this.buildSections();
		});
	}

	private fmtShort(dat: string): string {
		return dat.slice(8, 10) + '/' + dat.slice(5, 7);
	}

	// ============================================================
	// Construction des sections (un EDT actif du magasin = une grille)
	// ============================================================

	// Verrou de paie et note de journée dépendent du MAGASIN choisi : recalculés à chaque
	// construction des sections, pas au chargement.
	private refreshDayFlags(): void {
		const store = this.storeCtrl.value;
		for (const day of this.weekDays) {
			day.locked = this.locks.some(l => HrLock.covers(l, day.dat, store));
			// En « Tous », les notes des établissements se lisent ENSEMBLE, chacune préfixée de son
			// magasin : une note du jour ne doit pas disparaître faute d'établissement sélectionné.
			day.note = store != null
				? this.notes.find(n => n.dat === day.dat && n.id_store === store)?.txt ?? ''
				: this.notes.filter(n => n.dat === day.dat)
					.map(n => {
						const name = this.stores.find(s => s.id === n.id_store)?.name ?? '';
						return name ? name + ' : ' + n.txt : n.txt;
					})
					.join('\n');
		}
	}

	// ============================================================
	// Frise horaire : échelle commune + repères d'ouverture par jour
	// ============================================================

	private refreshTimeline(effective: EffectiveSlot[], store: number | null): void {
		// Les services du magasin affiché portent les horaires d'ouverture (hr_activity.data.hours).
		// En « Tous », l'amplitude est celle de TOUTE l'organisation : la frise doit contenir aussi
		// bien l'établissement qui ouvre à 8h que celui qui ferme à 20h.
		const storeActivities = this.activities.filter(a => (store == null || a.id_store === store) && a.is_active !== false);
		const storeSlots = effective.filter(s => (store == null || s.id_store === store) && !s.absent);

		// Toutes les ouvertures de la semaine entrent dans le calcul de l'échelle : sinon un
		// samedi qui ferme plus tôt rétrécirait la frise des autres jours.
		const allOpenings = this.weekDays.flatMap(day =>
			storeActivities.flatMap(a => hrOpeningOf(a, isoWeekdayOf(day.dat))));

		this.timeWin = timelineWindow(storeSlots, allOpenings, HR_GRID_START_MIN, HR_GRID_END_MIN);
		this.ticks = timelineTicks(this.timeWin);

		for (const day of this.weekDays) {
			const weekday = isoWeekdayOf(day.dat);
			// Plages fusionnées des services : deux services ouverts en même temps ne doivent pas
			// afficher deux fois le même repère.
			const openings = mergeRanges(storeActivities.flatMap(a => hrOpeningOf(a, weekday)));
			day.open = openings.length > 0;			// aucun horaire d'ouverture = magasin fermé ce jour
			day.markers = openingMarkers(openings, this.timeWin);
			day.bands = openingBands(openings, this.timeWin);
			// « 09:00 – 12:00, 14:00 – 19:00 » : l'amplitude en toutes lettres, pour l'impression.
			day.hours = openings.map(o => minutesLabel(o.start_min) + ' – ' + minutesLabel(o.end_min)).join(', ');
		}
	}

	// Géométrie d'un créneau sur la frise — la LONGUEUR reflète le temps de travail.
	barOf(s: { start_min: number; end_min: number }): TimelineBar {
		return barGeometry(s.start_min, s.end_min, this.timeWin);
	}

	// ============================================================
	// Couleur des barres — elle porte ce que la LIGNE ne dit pas
	// ============================================================
	//
	// Vue Employés : la ligne dit QUI, la barre dit donc dans quel SERVICE.
	// Vue Postes   : la ligne dit quel service, la barre dit donc QUI.
	// Un créneau qui dépasse l'horaire du magasin garde sa couleur : dépasser est NORMAL,
	// ce n'est pas une anomalie à signaler.

	slotColor(s: EffectiveSlot): string {
		const activity = this.activities.find(a => a.id === s.id_activity);
		return hrActivityColor(activity, HR_PERSON_COLORS);
	}

	employeeColor(id_employee: number): string {
		return hrEmployeeColor(this.employees.find(e => e.id === id_employee), HR_PERSON_COLORS);
	}


	private buildSections(): void {
		const store = this.storeCtrl.value;
		const to = addDays(this.monday, 6);
		this.refreshDayFlags();
		// Le moteur reçoit TOUS les emplois du temps : une exception ADD peut viser l'EDT d'un autre
		// magasin, il faut la résoudre correctement (elle s'affichera dans la section de SON EDT).
		const effective = effectiveSlotsForRange(this.monday, to, this.schedules, this.overrides, this.absences);
		this.allEffective = effective;			// mémorisé pour l'empreinte de publication (L6)
		// Le dimanche précédent (chargé avec la semaine) : sa projection ne sert qu'au contrôle du
		// repos quotidien à cheval sur les deux semaines — l'empreinte de publication n'en veut pas.
		this.prevDayEffective = effectiveSlotsForRange(
			addDays(this.monday, -1), addDays(this.monday, -1), this.schedules, this.overrides, this.absences);
		this.refreshTimeline(effective, store);

		this.sections = this.schedules
			.filter(s => (store == null || s.id_store === store) && s.is_active !== false)
			.map(sch => this.buildSection(sch, effective));
		this.buildPosteRows(effective, store);
		this.buildEmployeeRows(effective, store);
		this.refreshDayActivity(effective);
	}

	// ============================================================
	// Vue par SALARIÉ — « où travaille-t-il à chaque instant ? »
	// ============================================================
	//
	// Une ligne par personne, tous services et magasins confondus sur la même ligne : lundi matin
	// à La Seyne, l'après-midi à Ollioules… La couleur porte le SERVICE, le libellé de barre le lieu.

	private buildEmployeeRows(effective: EffectiveSlot[], store: number | null): void {
		const team = this.teams.find(t => t.id === this.teamCtrl.value);
		const ids = new Set<number>();
		for (const e of this.employees) {
			if (e.is_active === false) continue;
			// « Tous » : tout l'effectif actif, sans distinction d'établissement — c'est le défaut.
			if (store == null || e.id_store === store || (e.mobility ?? []).includes(store)) ids.add(e.id);
		}
		for (const s of effective) if (store == null || s.id_store === store) ids.add(s.id_employee);
		for (const id of this.extraEmployeeIds) ids.add(id);

		// Un salarié ajouté À LA MAIN passe outre le filtre d'équipe : sans cela, choisir un renfort
		// hors équipe le faisait disparaître aussitôt et l'ajout semblait sans effet.
		const shown = Array.from(ids)
			.filter(id => !team || team.members.includes(id) || this.extraEmployeeIds.has(id));

		// L'ordre RANGÉ À LA MAIN d'abord. L'alphabet ne disparaît pas, il redevient ce qu'il aurait
		// toujours dû être : le repli pour qui n'a pas encore de rang — une embauche de la semaine,
		// un renfort ajouté à l'instant. Elle se range après les autres, sans bousculer personne.
		this.employeeRows = rosterOrderOf(shown, this.rosterData, rest => rest
			.slice()
			.sort((a, b) => this.employeeNameOf(a).localeCompare(this.employeeNameOf(b))))
			.map(id => this.buildEmployeeRow(id, effective));
	}

	private buildEmployeeRow(empId: number, effective: EffectiveSlot[]): EmployeeRow {
		const emp = this.employees.find(e => e.id === empId);
		const cells: EmployeeCell[] = this.weekDays.map(day => {
			const absence = this.absences.find(a => a.id_employee === empId && HrAbsence.covers(a, day.dat)
				&& (a.state === HR_ABSENCE_STATE_APPROVED || a.state === HR_ABSENCE_STATE_REQUESTED));
			return {
				dat: day.dat,
				slots: effective.filter(s => s.id_employee === empId && s.dat === day.dat).sort((a, b) => a.start_min - b.start_min),
				absence: absence ? this.absenceBadgeOf(absence) : null,
			};
		});
		// Les alertes légales et le total regardent TOUTE la semaine du salarié (tous EDT confondus).
		return {
			id_employee: empId,
			name: hrEmployeeNameOf(emp, this.users),
			cells,
			...this.weekFigures(empId, effective.filter(s => s.id_employee === empId), emp),
		};
	}

	// L'ASSIETTE de la semaine, au sens légal — le calcul unique partagé avec le compteur d'heures
	// et le rapport de paie (hr-worktime.ts). Deux conséquences visibles ici :
	//   — une semaine de congés payés ne creuse plus « −35 h » : ses heures entrent dans l'assiette
	//     (Cass. soc. 10 sept. 2025), donc le contrat est couvert ;
	//   — un temps partiel voit ses heures COMPLÉMENTAIRES dès la première, pas à partir de 35 h.
	// L'absence APPROUVÉE qui couvre ce jour pour cette personne.
	private approvedAbsenceOf(empId: number, dat: string): HrAbsence | undefined {
		return this.absences.find(a => a.id_employee === empId && a.state === HR_ABSENCE_STATE_APPROVED && HrAbsence.covers(a, dat));
	}

	// Les jours d'absence DÉCOMPTÉS de la semaine, rangés par nature — c'est ce que le compteur de
	// la semaine affiche à côté des heures :
	//   `leave`      congés payés et assimilés : neutralisent la semaine (valorisés à la base
	//                journalière), et entrent dans l'assiette des heures supplémentaires ;
	//   `justified`  absence justifiée dont les heures comptent comme du travail (accident du
	//                travail, formation…) — type marqué `counted` ;
	//   `unjustified` absence non assimilée : congé sans solde, absence injustifiée. Elle creuse
	//                légitimement le compteur — type marqué `assimilated: false`.
	private absenceDaysByKindOf(empId: number, from: string, to: string, holidays: Map<string, string>): {
		leave: number; justified: number; unjustified: number;
	} {
		const mine = this.absences.filter(a => a.id_employee === empId && a.state === HR_ABSENCE_STATE_APPROVED);
		const pick = (test: (data: any) => boolean) => hrCountedLeaveDays(
			mine.filter(a => {
				const typ = this.absenceTypes.find(t => t.id === (a.typ ?? -1));
				return !!typ && test(typ.data ?? {});
			}), from, to, holidays,
		).reduce((sum, d) => sum + d.days, 0);

		return {
			leave: pick(d => d.counted !== true && d.assimilated !== false),
			justified: pick(d => d.counted === true),
			unjustified: pick(d => d.counted !== true && d.assimilated === false),
		};
	}

	// Les fériés de la semaine affichée. Le CALENDRIER DE L'ÉTABLISSEMENT (source officielle
	// Etalab, décisions d'ouverture comprises) fait foi dès qu'il est rempli ; sinon on retombe sur
	// le calcul local, qui connaît les 11 fériés de métropole.
	private holidaysOfWeek(): Map<string, string> {
		const to = addDays(this.monday, 6);
		const fromRules = holidayMapOf(this.rules.holidays);
		if (fromRules.size > 0) return fromRules;
		return new Map([...frenchHolidaysOf(+this.monday.slice(0, 4)), ...frenchHolidaysOf(+to.slice(0, 4))]);
	}

	private weekFigures(empId: number, slots: EffectiveSlot[], emp: HrEmployee | undefined): {
		total_min: number; counts_hours: boolean; assimilated_min: number; assimilated_days: number;
		holiday_days: number; holiday_min: number; justified_days: number;
		unjustified_days: number; basis_min: number; contract_min: number | null;
		delta_min: number | null; extra_min: number; extra_kind: 'overtime' | 'complementary'; issues: LegalIssue[];
	} {
		const closedHolidayDats = new Set(closedHolidaysInRange(this.rules.holidays, this.monday, addDays(this.monday, 6)).map(d => d.dat));
		const empWeek = slots.map(s => ({
			weekday: isoWeekdayOf(s.dat), start_min: s.start_min, end_min: s.end_min, break_min: s.break_min, absent: s.absent,
		}));
		// Le total AFFICHÉ reste celui du planning (ce qui est à l'écran), mais les heures d'un
		// férié chômé sortent de l'assiette « travaillé » pour être comptées en heures maintenues.
		const totalMin = plannedMinutes(empWeek);
		const workedMin = plannedMinutes(slots.filter(s => !closedHolidayDats.has(s.dat)).map(s => ({
			start_min: s.start_min, end_min: s.end_min, break_min: s.break_min, absent: s.absent,
		})));

		// Heures de planning effacées par une absence ASSIMILÉE (congés payés, sauf type marqué autrement).
		let assimilatedPlanned = 0;
		for (const s of slots) {
			if (!s.absent) continue;
			const typ = this.absenceTypes.find(t => t.id === (this.approvedAbsenceOf(empId, s.dat)?.typ ?? -1));
			if (!typ || typ.data?.counted === true || typ.data?.assimilated === false) continue;
			assimilatedPlanned += Math.max(0, (s.end_min - s.start_min) - (s.break_min ?? 0));
		}

		// JOURS de congés décomptés de la semaine — indépendants du planning, qui peut ne rien
		// contenir ces jours-là. C'est eux que le moteur valorise à la base journalière (7 h pour
		// un contrat de 35 h sur 5 jours), et c'est ce qui fait dire « 3 j de CP », pas 4 cases.
		const to = addDays(this.monday, 6);
		const holidays = this.holidaysOfWeek();
		const kinds = this.absenceDaysByKindOf(empId, this.monday, to, holidays);
		// Jours effectivement TRAVAILLÉS : ils bornent les jours de congés (5 jours ouvrés − 2
		// travaillés = 3 jours à combler), ce qui évite de valoriser un jour de repos en congé.
		const workedDays = new Set(slots.filter(s => !s.absent && !closedHolidayDats.has(s.dat)).map(s => s.dat)).size;
		// Fériés CHÔMÉS de la semaine : leurs heures sont maintenues (L3133-1), donc valorisées à la
		// base journalière — sinon chaque 14 juillet afficherait « −7 h ». Le plafond hebdomadaire
		// écarte de lui-même un férié qui tombe alors que les jours ouvrés sont déjà faits.
		// Les créneaux d'un férié CHÔMÉS ne sont pas du travail (personne n'est venu) mais leurs
		// heures restent acquises : on les sort du travaillé et on les valorise à part, JOUR PAR
		// JOUR — c'est le planning qui dit ce que pesait ce 14 juillet, pas une moyenne.
		const contractMinForBase = emp?.contract_hours_week != null ? Math.round(emp.contract_hours_week * 60) : null;
		const resolvedDayBase = effectiveWorkRules(contractMinForBase, this.rules, employeeWorkProfileOf(emp?.data)).day_base_min;
		const closedHolidays = closedHolidaysInRange(this.rules.holidays, this.monday, to);
		const holidayDays = closedHolidays.length;
		let holidayMin = 0;
		for (const day of closedHolidays) {
			const planned = plannedMinutes(slots.filter(s => s.dat === day.dat).map(s => ({
				start_min: s.start_min, end_min: s.end_min, break_min: s.break_min, absent: s.absent,
			})));
			holidayMin += planned > 0 ? planned : resolvedDayBase;
		}

		const contractMin = emp?.contract_hours_week != null ? Math.round(emp.contract_hours_week * 60) : null;
		const resolved = effectiveWorkRules(contractMin, this.rules, employeeWorkProfileOf(emp?.data));
		const outcome = weekOutcome({
			monday: this.monday, worked_min: workedMin, assimilated_min: assimilatedPlanned,
			assimilated_days: kinds.leave, worked_days: workedDays,
			holiday_days: holidayDays, holiday_min: holidayMin, reference_min: contractMin ?? 0,
		}, resolved);

		// HORS DÉCOMPTE / FORFAIT JOURS — la personne est planifiée (savoir qui tient la boutique
		// samedi a du sens pour un gérant comme pour un cadre au forfait), mais AUCUN total ne la
		// concerne : ni heures de la semaine, ni écart au contrat, ni alerte de durée maximale.
		// Le moteur rend déjà tout à zéro ; on efface ici le seul chiffre qu'il ne connaît pas,
		// le total affiché — et on coupe les alertes de `legalIssuesForWeek`, qui ne sait pas
		// que L3121-18 ne s'applique pas à un cadre dirigeant.
		if (!resolved.counts_hours) {
			return {
				total_min: totalMin, counts_hours: false,
				assimilated_min: 0, assimilated_days: 0, holiday_days: 0, holiday_min: 0,
				justified_days: kinds.justified, unjustified_days: kinds.unjustified,
				basis_min: 0, contract_min: null, delta_min: null,
				extra_min: 0, extra_kind: 'overtime', issues: [],
			};
		}

		return {
			total_min: totalMin,
			counts_hours: true,
			assimilated_min: outcome.assimilated_min,
			assimilated_days: outcome.assimilated_days,
			holiday_days: outcome.holiday_days,
			holiday_min: outcome.holiday_min,
			justified_days: kinds.justified,
			unjustified_days: kinds.unjustified,
			basis_min: outcome.basis_min,
			contract_min: contractMin,
			// L'écart se mesure sur l'ASSIETTE : un congé payé ne crée plus d'heures manquantes.
			delta_min: contractMin != null && contractMin > 0 ? outcome.counter_delta_min : null,
			extra_min: outcome.extra_min,
			extra_kind: outcome.kind,
			// `legalIssuesForWeek` ne reçoit plus le contrat : le dépassement de seuil est calculé
			// par le moteur légal, qui sait distinguer heures supplémentaires et complémentaires.
			issues: [
				...legalIssuesForWeek(empWeek, null),
				// Repos quotidien dimanche → lundi : le dimanche PRÉCÉDENT est projeté à part
				// (prevDayEffective) — d'une semaine isolée, « dimanche 23 h → lundi 6 h » passait.
				...crossWeekRestIssues(
					this.prevDayEffective
						.filter(s => s.id_employee === empId)
						.map(s => ({ weekday: 7, start_min: s.start_min, end_min: s.end_min, absent: s.absent })),
					empWeek),
				...outcome.issues.map(i => ({ code: i.code, weekday: null, detail: i.detail } as LegalIssue)),
			],
		};
	}

	// « Ollioules — Vente » : le lieu complet, pour l'infobulle de la barre.
	placeLabel(s: EffectiveSlot): string {
		const store = this.stores.find(st => st.id === s.id_store)?.name ?? '';
		const act = this.activities.find(a => a.id === s.id_activity)?.name ?? '';
		return [store, act].filter(Boolean).join(' — ') || 'Service';
	}

	// Libellé court porté PAR la barre en vue salarié : le MAGASIN (« Ollioules »), jamais le
	// service. La ligne dit déjà QUI, la couleur dit le service — et comme chaque établissement a
	// son propre service « Magasin », l'écrire donnait sept barres identiques sur la même ligne
	// alors que la question est justement « il est où, lundi après-midi ? ».
	placeShort(s: EffectiveSlot): string {
		return this.stores.find(st => st.id === s.id_store)?.name
			?? this.activities.find(a => a.id === s.id_activity)?.name ?? '';
	}

	// Deuxième ligne de la barre : le POSTE. La première dit le LIEU — la couleur seule ne nommait
	// pas le service, et « Ollioules » ne dit pas si on y vend ou si on y livre. Vide quand le
	// libellé du poste est déjà celui de la première ligne (magasin inconnu).
	activityShort(s: EffectiveSlot): string {
		const act = this.activities.find(a => a.id === s.id_activity)?.name ?? '';
		return act === this.placeShort(s) ? '' : act;
	}

	// Une journée d'absence se lit d'un BLOC : dès qu'il ne reste aucun créneau travaillé, elle
	// occupe la journée entière au lieu d'une pastille posée à côté de barres barrées.
	//
	// Une demande NON DÉCIDÉE occupe le bloc de la même façon — hachurée : le responsable qui
	// construit sa semaine doit voir les congés demandés au moment où il les place, pas seulement
	// une fois validés. Ce qu'elle ne fait pas, c'est retirer des heures : le moteur laisse ces
	// créneaux `absent: false`, et couverture, compteur et paie continuent de compter le travail dû.
	fullDayAbsence(cell: { slots: EffectiveSlot[]; absence: AbsenceBadge | null }): AbsenceBadge | null {
		if (!cell.absence) return null;
		return cell.slots.every(s => s.absent || s.absence_pending) ? cell.absence : null;
	}

	// L'horaire que l'absence remplace ne disparaît pas : il passe en infobulle du bloc.
	absenceTooltip(cell: { slots: EffectiveSlot[]; absence: AbsenceBadge | null }): string {
		const badge = this.fullDayAbsence(cell);
		if (!badge) return '';
		const planned = cell.slots.map(s => this.slotLabel(s)).join(', ');
		return badge.label + (badge.pending ? ' — demandé, pas encore validé' : '')
			+ (planned ? '\nPrévu ce jour : ' + planned : '');
	}

	// ============================================================
	// Jours fermés — masqués par défaut, réaffichés par le toggle
	// ============================================================

	private refreshDayActivity(effective: EffectiveSlot[]): void {
		const store = this.storeCtrl.value;
		for (const day of this.weekDays) {
			day.hasActivity = effective.some(s => (store == null || s.id_store === store) && s.dat === day.dat)
				|| this.notes.some(n => n.dat === day.dat && (store == null || n.id_store === store));
		}
	}

	// Index (0..6) des jours affichés : tous si le toggle est actif, sinon on masque les jours
	// fermés ET sans activité. On raisonne en INDEX pour garder l'alignement weekDays ↔ row.cells.
	get visibleDayIndices(): number[] {
		const all = this.weekDays.map((_, i) => i);
		const visible = all.filter(i => this.showClosedDays || this.weekDays[i].open || this.weekDays[i].hasActivity);
		// Aucun service n'a d'horaires saisis : tout serait « fermé » et la grille disparaîtrait.
		return visible.length > 0 ? visible : all;
	}

	get hiddenDaysCount(): number {
		return this.weekDays.filter(d => !d.open && !d.hasActivity).length;
	}

	onToggleClosedDays(show: boolean): void {
		this.showClosedDays = show;
	}

	// ============================================================
	// P04 — vue par POSTE : le planning dans l'autre sens
	// ============================================================
	//
	// « Combien de personnes sur le magasin, et qui ? » plutôt que « que fait untel ? ».
	// C'est la vue qui répond à « il faut toujours au moins une personne par boutique » :
	// une ligne par service, et le compte par jour saute aux yeux.
	//
	// Structure SÉPARÉE de `sections` : contorsionner PlanningRow (dont les cellules sont
	// indexées par employé) aurait rendu les deux vues illisibles.

	private buildPosteRows(effective: EffectiveSlot[], store: number | null): void {
		const byActivity = new Map<number, { name: string; store: string; seq: number; slots: EffectiveSlot[] }>();
		const storeName = (id: number | null) => this.stores.find(s => s.id === id)?.name ?? '';
		// En « Tous », le nom du service ne suffit plus à le désigner : chaque établissement a son
		// « Magasin ». On le préfixe du lieu, exactement comme le libellé des barres.
		const label = (name: string, id: number | null) =>
			store == null && storeName(id) ? storeName(id) + ' — ' + name : name;

		// Les lignes sont les POSTES du magasin — TOUS, y compris ceux que personne ne tient cette
		// semaine : « qui tient ce poste ? » se pose surtout quand la réponse est « personne ».
		// Les tirer des emplois du temps n'en montrait qu'un par grille existante.
		for (const a of this.activities.filter(a => (store == null || a.id_store === store) && a.is_active !== false)) {
			byActivity.set(a.id, { name: label(a.name, a.id_store), store: storeName(a.id_store), seq: a.seq ?? 0, slots: [] });
		}

		// Les créneaux arrivent par leur emploi du temps, qui porte le service.
		for (const sch of this.schedules.filter(s => (store == null || s.id_store === store) && s.is_active !== false)) {
			const idActivity = hrScheduleActivityId(sch) ?? sch.id;
			if (!byActivity.has(idActivity)) {
				// Service désactivé ou absent de la liste : on garde quand même la ligne, sinon ses
				// créneaux disparaîtraient du planning sans que personne ne le voie.
				byActivity.set(idActivity, { name: label(sch.name, sch.id_store), store: storeName(sch.id_store), seq: 999, slots: [] });
			}
			byActivity.get(idActivity)!.slots.push(...effective.filter(s => s.id_schedule === sch.id));
		}

		this.posteRows = Array.from(byActivity.entries())
			// Les services d'un même établissement restent GROUPÉS (le lieu d'abord), et à l'intérieur
			// dans l'ordre de l'écran Organisation. Sur un seul magasin, le tri se réduit au `seq`.
			.sort(([, a], [, b]) => a.store.localeCompare(b.store) || a.seq - b.seq || a.name.localeCompare(b.name))
			.map(([id_activity, { name, seq, slots }]) => {
				const cells = this.weekDays.map(day => {
					const daySlots = slots
						.filter(s => s.dat === day.dat && !s.absent)
						.sort((a, b) => a.start_min - b.start_min);
					return {
						dat: day.dat,
						entries: daySlots.map(s => ({
							slot: s,
							name: hrEmployeeNameOf(this.employees.find(e => e.id === s.id_employee), this.users),
						})),
						// Le compte de PERSONNES distinctes : deux créneaux d'une même personne
						// (coupure méridienne) ne font pas deux présents.
						people: new Set(daySlots.map(s => s.id_employee)).size,
						minutes: plannedMinutes(daySlots),
					};
				});
				return {
					id_activity,
					name,
					seq,
					color: hrActivityColor(this.activities.find(a => a.id === id_activity), HR_PERSON_COLORS),
					cells,
					total_min: cells.reduce((sum, c) => sum + c.minutes, 0),
				};
			});
	}

	// Pied de colonne de la vue Postes : « 11 pers. — 83h » pour la journée, tous services confondus.
	dayTotals(dayIdx: number): { people: number; minutes: number } {
		const dat = this.weekDays[dayIdx]?.dat ?? '';
		const daySlots = this.allEffective.filter(s => s.dat === dat && !s.absent
			&& this.posteRows.some(r => r.cells[dayIdx]?.entries.some(e => e.slot === s)));
		return {
			people: new Set(daySlots.map(s => s.id_employee)).size,
			minutes: plannedMinutes(daySlots),
		};
	}

	onViewMode(mode: 'employees' | 'postes'): void {
		this.viewMode = mode;
	}

	// ============================================================
	// Ajout naturel d'un salarié — ligne « + salarié » (remplace « Renfort… »)
	// ============================================================

	// Candidats : qui peut-on faire venir ? Justement CEUX QUI NE SONT PAS LÀ — le renfort d'un autre
	// magasin, la personne écartée par le filtre d'équipe. Restreindre les candidats au magasin courant
	// (rattachement ou mobilité) reprenait mot pour mot le critère de buildEmployeeRows : tout candidat
	// était donc déjà affiché et la liste restait vide en permanence.
	// Les gens du magasin d'abord, les autres ensuite, suffixés de leur magasin pour lever l'ambiguïté.
	get addableEmployees(): { id: number; name: string }[] {
		const store = this.storeCtrl.value;
		// Vue Poste : les lignes sont des SERVICES, pas des personnes — personne n'y est « déjà
		// affiché », et placer un deuxième créneau à quelqu'un qui tient déjà un poste est légitime.
		const shown = this.viewMode === 'employees'
			? new Set(this.employeeRows.map(r => r.id_employee))
			: new Set<number>();
		const local = (e: HrEmployee) => store == null || e.id_store === store || (e.mobility ?? []).includes(store);
		return this.employees
			.filter(e => e.is_active !== false && !shown.has(e.id))
			.map(e => {
				const name = hrEmployeeNameOf(e, this.users);
				// En « Tous », personne n'est « d'ailleurs » : le magasin est alors écrit pour TOUT le
				// monde, c'est justement ce qui distingue deux homonymes de deux établissements.
				const from = store == null || !local(e) ? this.stores.find(s => s.id === e.id_store)?.name ?? '' : '';
				return { id: e.id, name: from ? name + ' — ' + from : name, local: local(e) };
			})
			.sort((a, b) => Number(b.local) - Number(a.local) || a.name.localeCompare(b.name))
			.map(({ id, name }) => ({ id, name }));
	}

	// Choisir une personne dans la ligne d'ajout. Vue Salarié : la ligne apparaît, prête à recevoir
	// des créneaux (aucune écriture serveur tant qu'aucun créneau n'est posé). Vue Poste : les lignes
	// sont des services (pas des personnes) — on ouvre directement le placement d'un créneau.
	onAddEmployee(id: number | null): void {
		if (id == null || !this.isHrManager) return;
		this.addEmpCtrl.setValue(null, { emitEvent: false });
		if (this.viewMode === 'employees') {
			this.extraEmployeeIds.add(id);
			this.buildSections();
		} else {
			const emp = this.employees.find(e => e.id === id);
			this.openOverrideDialog({
				pickEmployee: true, id_employee: id, scheduleName: '',
				theoreticalSlots: [], overrides: [],
				employees: [{ id, name: hrEmployeeNameOf(emp, this.users) }],
				weekDays: this.weekDays.map(d => ({ dat: d.dat, label: d.label })),
				// Placer quelqu'un sur un poste, c'est justement choisir LEQUEL.
				places: this.placesFor(id, this.monday),
				id_schedule: this.defaultScheduleFor(id),
			}, this.defaultScheduleFor(id));
		}
	}

	// ============================================================
	// ORDRE DES LIGNES — ranger son équipe à la main, une fois pour toutes
	// ============================================================
	//
	// Le même geste que dans « Horaires types » (et que les livraisons d'une tournée) : on prend une
	// ligne par sa poignée, on la monte ou on la descend, elle reste où on l'a mise. L'alphabet ne
	// dit rien d'une équipe ; l'ordre, lui, se lit d'un coup d'œil quand il est celui du terrain.
	//
	// CE QUI SE DÉPLACE EST LA LIGNE ELLE-MÊME, à la taille de la grille (hr-drag-band.ts) : la
	// personne emmène ses heures, et c'est bien ce qu'on voit monter et descendre.
	//
	// L'ordre est ENREGISTRÉ dans c_values 30, sous la même forme que celui d'un emploi du temps
	// (`member_seq`, hr-roster.ts). Il vaut pour tout le planning : semaine suivante, magasin
	// suivant, autre poste de travail — on ne range pas son équipe deux fois.

	// La bande se relève AU CLIC (le CDK fabrique son aperçu au premier mouvement, avec les valeurs
	// qu'il trouve alors) : toutes les cases de la ligne, du nom au total. Ce que chaque case doit
	// dessiner est décidé ici plutôt que dans le gabarit — l'aperçu se redessine à chaque pixel de
	// glissé, il n'a pas à recalculer sept fois par image ce qui ne bouge plus.
	onRowGrab(ev: PointerEvent): void {
		const band = dragBandOfRow(ev.currentTarget, 'td', ev.clientX, ev.clientY);
		if (!band) return;
		const kindOf = (i: number): 'name' | 'day' | 'total' =>
			i === 0 ? 'name' : i <= this.visibleDayIndices.length ? 'day' : 'total';
		this.rowBand = band;
		this.bandCells = band.cells.map((c, i) => ({
			kind: kindOf(i), dayIdx: this.visibleDayIndices[i - 1] ?? -1, left: c.left, width: c.width,
		}));
	}

	// `zone.run` : le CDK annonce le DÉPART du glissé hors de la zone Angular (à la différence du
	// dépôt) — sans cela, l'affichage n'apprendrait la nouvelle qu'au prochain événement venu.
	onRowLift(row: EmployeeRow): void {
		this.zone.run(() => this.movingId = row.id_employee);
	}

	onRowDropped(): void {
		this.zone.run(() => this.movingId = null);
	}

	// Le dépôt. L'écran est presque toujours FILTRÉ (un magasin, une équipe) : le déplacement ne
	// change donc la place que des personnes visibles, celles qui sont hors écran ne bougent pas
	// d'un cran (rosterMoveVisible). Et l'ordre complet est réécrit avec TOUS ceux qui avaient déjà
	// un rang — sans quoi ranger le planning d'un magasin effacerait celui de tous les autres.
	onRowDrop(event: CdkDragDrop<EmployeeRow[]>): void {
		const { previousIndex, currentIndex } = event;
		if (previousIndex === currentIndex || !this.isHrManager) return;

		const before = this.rosterData;
		const visible = this.employeeRows.map(r => r.id_employee);
		// L'effectif ACTIF, plus ceux qui ont déjà un rang : un ancien salarié garde sa place tant
		// qu'on la lui a donnée, mais on n'en attribue pas à qui ne paraîtra plus jamais à l'écran —
		// le référentiel finirait par porter tous les départs de l'entreprise.
		const universe = rosterUniverse(this.employees.filter(e => e.is_active !== false).map(e => e.id), this.rosterData);
		const full = rosterOrderOf(universe, this.rosterData, rest => rest
			.slice()
			.sort((a, b) => this.employeeNameOf(a).localeCompare(this.employeeNameOf(b))));

		this.rosterData = withRosterSeq(this.rosterData, rosterMoveVisible(full, visible, previousIndex, currentIndex));
		// La ligne se pose TOUT DE SUITE à sa place : l'aller-retour serveur ne doit pas se voir.
		// La grille n'est pas reconstruite pour autant — rien d'autre que l'ordre n'a changé.
		moveItemInArray(this.employeeRows, previousIndex, currentIndex);
		this.saveRoster(before);
	}

	// Enregistrement du seul ordre. En cas de refus (droits, réseau), on remet l'écran DANS L'ÉTAT
	// DE LA BASE : une ligne qui reste où on l'a lâchée alors que rien n'a été enregistré est un
	// mensonge qu'on ne découvrirait qu'au prochain chargement.
	private saveRoster(before: RosterData | null): void {
		const elements = [{ id: 1, seq: 1, name: 'Ordre d\'affichage', is_active: true, data: this.rosterData }];
		const undo = (msg: string) => {
			this.rosterData = before;
			this.buildSections();
			this.alertService.error(msg);
		};
		this.hrService.postValuesElements(C_VALUES_HR_ROSTER, elements, 'Ordre des salariés au planning').subscribe({
			next: () => this.valuesService.updateData(),
			// Refus métier (droits — postHrEvent le convertit en erreur RxJS) comme vraie panne :
			// l'écran revient à l'ordre que la base connaît.
			error: (e) => undo(hrErrorMessage(e)),
		});
	}

	// EDT « d'attache » d'un salarié sur le magasin courant : celui dont il est membre, sinon le premier.
	// En « Tous », le magasin de référence est celui du SALARIÉ : sans lui, poser un créneau
	// l'aurait planifié sur l'emploi du temps du premier établissement venu.
	private defaultScheduleFor(empId: number): number {
		const store = this.storeCtrl.value ?? this.employees.find(e => e.id === empId)?.id_store ?? null;
		const storeScheds = this.schedules.filter(s => (store == null || s.id_store === store) && s.is_active !== false);
		return (storeScheds.find(s => (s.members ?? []).includes(empId)) ?? storeScheds[0])?.id ?? 0;
	}

	// LES LIEUX où cette personne peut travailler ce jour-là : un emploi du temps = un établissement
	// × un service. C'est ce que le dialogue de journée doit proposer, sinon « ajouter un créneau »
	// le pose en silence sur le premier EDT du magasin affiché — et une personne mobile se retrouve
	// planifiée ailleurs que là où elle est attendue.
	// Ordre : le magasin affiché d'abord (le cas courant), les autres ensuite.
	private placesFor(empId: number, dat: string): OverridePlace[] {
		const emp = this.employees.find(e => e.id === empId);
		// En « Tous », aucun magasin n'est affiché : c'est celui du salarié qui prend la tête de liste.
		const store = this.storeCtrl.value ?? emp?.id_store ?? null;
		// Établissements ouverts à cette personne : son rattachement et sa mobilité. Sans rien de
		// renseigné, on n'enferme pas le gérant : tous les établissements restent proposés.
		const allowed = new Set<number>([...(emp?.mobility ?? [])]);
		if (emp?.id_store != null) allowed.add(emp.id_store);

		return this.schedules
			.filter(s => scheduleAppliesOn(s, dat) && s.id_store != null
				&& (allowed.size === 0 || allowed.has(s.id_store)))
			.map(s => {
				const storeName = this.stores.find(st => st.id === s.id_store)?.name ?? '';
				const act = this.activities.find(a => a.id === hrScheduleActivityId(s))?.name ?? '';
				return {
					id_schedule: s.id,
					// « Ollioules — Livraison » ; le nom de l'EDT en repli s'il manque une pièce.
					label: [storeName, act].filter(Boolean).join(' — ') || s.name,
					store: storeName,
				};
			})
			.sort((a, b) => Number(b.store === (this.stores.find(st => st.id === store)?.name ?? ''))
				- Number(a.store === (this.stores.find(st => st.id === store)?.name ?? ''))
				|| a.label.localeCompare(b.label));
	}

	// Libellé du lieu d'un emploi du temps — le même vocabulaire partout (barres, dialogue, liste).
	private placeLabelOfSchedule(idSchedule: number): string {
		const s = this.schedules.find(x => x.id === idSchedule);
		if (!s) return '';
		const storeName = this.stores.find(st => st.id === s.id_store)?.name ?? '';
		const act = this.activities.find(a => a.id === hrScheduleActivityId(s))?.name ?? '';
		return [storeName, act].filter(Boolean).join(' — ') || s.name;
	}

	// Clic sur une case VIDE de la vue Salarié : éditer le planning du jour (dialog).
	onEmployeeCellClick(row: EmployeeRow, dayIdx: number): void {
		if (!this.isHrManager) return;			// consultation seule : le clic ne fait rien
		this.openEmployeeDay(row.id_employee, this.weekDays[dayIdx].dat, this.defaultScheduleFor(row.id_employee));
	}

	private openEmployeeDay(empId: number, dat: string, idSchedule: number): void {
		const emp = this.employees.find(e => e.id === empId);
		const day = this.weekDays.find(d => d.dat === dat);
		const data: OverrideDialogData = {
			employeeName: hrEmployeeNameOf(emp, this.users),
			id_employee: empId,
			dat,
			dayLabel: (day?.label ?? '') + (day?.holiday ? ' (' + day.holiday + ')' : ''),
			scheduleName: this.schedules.find(s => s.id === idSchedule)?.name ?? '',
			theoreticalSlots: this.allEffective
				.filter(s => s.id_employee === empId && s.dat === dat && s.origin === 'cycle')
				.map(s => ({ start_min: s.start_min, end_min: s.end_min, place: this.placeLabelOfSchedule(s.id_schedule) })),
			overrides: this.overrides.filter(o => o.id_employee === empId && o.dat === dat),
			places: this.placesFor(empId, dat),
			id_schedule: idSchedule,
		};
		this.openOverrideDialog(data, idSchedule);
	}

	// ============================================================
	// Redimensionnement / déplacement des barres (pointeur)
	// ============================================================
	//
	// Le planning est le RÉALISÉ : glisser une barre l'édite directement. La première fois qu'on touche
	// un créneau RÉGULIER d'une journée, celle-ci se fige en réel (materializeDay). Aucune notion
	// d'exception : un créneau édité ressemble à n'importe quel créneau du planning.

	private minuteAt(clientX: number, rect: DOMRect): number {
		const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / Math.max(1, rect.width)));
		const raw = this.timeWin.start_min + ratio * this.timeWin.span_min;
		return Math.max(this.timeWin.start_min, Math.min(this.timeWin.end_min,
			Math.round(raw / HR_GRID_SNAP_MIN) * HR_GRID_SNAP_MIN));
	}

	onBarPointerDown(e: PointerEvent, s: EffectiveSlot, track: HTMLElement, mode: 'move' | 'start' | 'end'): void {
		if (!this.isHrManager) return;								// consultation seule : les barres ne bougent pas
		if (e.button !== 0 || s.absent) return;						// une absence n'est pas manipulable
		if (this.isDayLocked(s.dat, s.id_store)) return;				// journée verrouillée pour la paie
		e.preventDefault();
		e.stopPropagation();
		const rect = track.getBoundingClientRect();
		this.drag = {
			slot: s, mode, rect, originMin: this.minuteAt(e.clientX, rect),
			before: { start_min: s.start_min, end_min: s.end_min }, moved: false,
		};
		this.bindDrag();
	}

	private bindDrag(): void {
		this.zone.runOutsideAngular(() => {
			window.addEventListener('pointermove', this.onMove);
			window.addEventListener('pointerup', this.onUp);
		});
	}

	private unbindDrag(): void {
		window.removeEventListener('pointermove', this.onMove);
		window.removeEventListener('pointerup', this.onUp);
	}

	private dragMove(e: PointerEvent): void {
		const d = this.drag;
		if (!d) return;
		const cur = this.minuteAt(e.clientX, d.rect);
		if (cur !== d.originMin) d.moved = true;
		const s = d.slot;
		if (d.mode === 'move') {
			const len = d.before.end_min - d.before.start_min;
			let start = d.before.start_min + (cur - d.originMin);
			start = Math.max(this.timeWin.start_min, Math.min(this.timeWin.end_min - len, start));
			s.start_min = start; s.end_min = start + len;
		} else if (d.mode === 'start') {
			s.start_min = Math.min(cur, d.before.end_min - HR_GRID_SNAP_MIN);
		} else {
			s.end_min = Math.max(cur, d.before.start_min + HR_GRID_SNAP_MIN);
		}
	}

	private dragEnd(_e: PointerEvent): void {
		const d = this.drag;
		this.drag = null;
		this.unbindDrag();
		if (!d) return;
		if (!d.moved) {													// simple clic sur la barre : édition au clavier
			this.openEmployeeDay(d.slot.id_employee, d.slot.dat, d.slot.id_schedule || this.defaultScheduleFor(d.slot.id_employee));
			return;
		}
		this.persistResize(d);
	}

	// Enregistre le créneau redimensionné. Déjà dans le planning (override) → simple mise à jour ;
	// créneau régulier → on fige d'abord la journée en réel.
	private persistResize(d: PlanningDragState): void {
		const s = d.slot;
		if (s.origin === 'override') {
			const ov = this.findAddOverride(s.id_employee, s.dat, s.id_schedule, d.before.start_min, d.before.end_min);
			if (!ov) { this.loadWeek(this.date); return; }				// désynchronisé : on recharge
			const updated = Object.assign(new HrOverride(), ov, { start_min: s.start_min, end_min: s.end_min });
			this.postOverride(EVENTTYP_UPDATE, updated);
		} else {
			this.materializeDay(s.id_employee, s.dat);
		}
	}

	// Fige une journée en planning réel : on envoie la journée COMPLÈTE (créneaux réguliers du cycle
	// + créneaux déjà ajoutés, la géométrie de celui qu'on vient d'éditer étant à jour dans
	// allEffective) et le serveur la resynchronise en UNE transaction — même logique que lnk_slots
	// sur un emploi du temps. Les autres salariés ne sont pas touchés.
	//
	// Auparavant : un REMOVE + N ADD en events PARALLÈLES, chacun dans sa propre transaction. Si un
	// ADD échouait (réseau, verrou de paie posé entre-temps), la journée restait à moitié écrite —
	// le REMOVE passé, les créneaux pas tous recréés : des heures disparaissaient sans un mot.
	//
	// DEMI-JOURNÉE D'ABSENCE : la journée est alors affichée en deux parts (matin travaillé,
	// après-midi masqué — cf. pushEffective). On les RECOLLE avant d'écrire, et on garde les parts
	// absentes : le planning doit rester la journée COMPLÈTE, c'est `hr_absence` qui la masque.
	// Sans ça, figer une journée y graverait une coupure à 13:00 que personne n'a saisie, et
	// l'après-midi disparaîtrait pour de bon si l'absence était ensuite annulée.
	private materializeDay(empId: number, dat: string): void {
		const day = this.recollectHalves(this.allEffective
			.filter(s => s.id_employee === empId && s.dat === dat)
			.sort((a, b) => a.start_min - b.start_min))
			// Une journée ENTIÈREMENT absente (congé classique) n'a rien à figer : on n'écrit pas
			// un planning à partir d'un jour où la personne n'était pas là.
			.filter(s => !s.absent);
		const payload = this.hrService.buildOverrideDayPayload({
			id_employee: empId,
			dat,
			slots: day.map(s => ({
				id_schedule: s.id_schedule || this.defaultScheduleFor(empId),
				start_min: s.start_min,
				end_min: s.end_min,
				break_min: s.break_min,
			})),
		});
		this.hrService.postHrEvent(EVENTTYP_HR_OVERRIDE_DAY, OBJECTTYP_HR_OVERRIDE, payload).subscribe({
			next: () => this.loadWeek(this.date),
			error: (e) => this.alertService.error(hrErrorMessage(e)),
		});
	}

	// Recolle les deux moitiés d'un créneau coupé par une demi-journée d'absence. Le créneau
	// reconstitué n'est « absent » que si ses DEUX parts le sont — sinon il reste du travail dedans.
	// Les créneaux ordinaires traversent sans être touchés : on ne fusionne QUE ce que la coupe a
	// séparé (`half_part`), jamais deux créneaux contigus réellement saisis comme tels.
	private recollectHalves(slots: EffectiveSlot[]): EffectiveSlot[] {
		const out: EffectiveSlot[] = [];
		for (const s of slots) {
			const prev = out[out.length - 1];
			if (prev && prev.half_part === 'morning' && s.half_part === 'afternoon'
				&& prev.id_schedule === s.id_schedule && prev.end_min === s.start_min) {
				out[out.length - 1] = {
					...prev,
					end_min: s.end_min,
					break_min: prev.break_min + s.break_min,
					absent: prev.absent && s.absent,
					absence_pending: prev.absence_pending && s.absence_pending,
					half_part: undefined,
				};
				continue;
			}
			out.push(s);
		}
		return out;
	}

	private findAddOverride(empId: number, dat: string, idSchedule: number, start: number, end: number): HrOverride | null {
		return this.overrides.find(o => o.typ === HR_OVERRIDE_ADD && o.id_employee === empId && o.dat === dat
			&& (o.id_schedule ?? 0) === idSchedule && o.start_min === start && o.end_min === end) ?? null;
	}

	// Le verrou qui compte est celui de l'établissement DU CRÉNEAU : en « Tous », se rabattre sur le
	// magasin affiché (aucun) aurait fait qu'une période verrouillée quelque part gèle tout le monde.
	private isDayLocked(dat: string, store: number | null = this.storeCtrl.value): boolean {
		return this.locks.some(l => HrLock.covers(l, dat, store));
	}

	private buildSection(sch: HrSchedule, effective: EffectiveSlot[]): ScheduleSection {
		const schedSlots = effective.filter(s => s.id_schedule === sch.id);
		const rowIds = new Set<number>([...(sch.members ?? []), ...schedSlots.map(s => s.id_employee)]);
		// O05 — filtre d'équipe : un groupe transverse restreint l'affichage sans rien changer
		// à l'organisation (« montre-moi seulement l'équipe livraison »).
		const team = this.teams.find(t => t.id === this.teamCtrl.value);
		const rows = Array.from(rowIds)
			.filter(empId => !team || team.members.includes(empId))
			.map(empId => this.buildRow(empId, sch, schedSlots, effective))
			.sort((a, b) => a.name.localeCompare(b.name));
		const cycle = Math.max(1, sch.cycle_weeks);
		return {
			schedule: sch,
			activityName: this.activities.find(a => a.id === hrScheduleActivityId(sch))?.name ?? '',
			cycleLabel: cycle > 1 ? ('Semaine ' + (weekIndexOf(this.monday, sch.anchor_monday, cycle) + 1) + '/' + cycle + ' du cycle') : '',
			rows,
		};
	}

	private buildRow(empId: number, sch: HrSchedule, schedSlots: EffectiveSlot[], allEffective: EffectiveSlot[]): PlanningRow {
		const emp = this.employees.find(e => e.id === empId);
		const cells: PlanningCell[] = this.weekDays.map(day => {
			const absence = this.absences.find(a => a.id_employee === empId && HrAbsence.covers(a, day.dat)
				&& (a.state === HR_ABSENCE_STATE_APPROVED || a.state === HR_ABSENCE_STATE_REQUESTED));
			return {
				dat: day.dat,
				slots: schedSlots.filter(s => s.id_employee === empId && s.dat === day.dat).sort((a, b) => a.start_min - b.start_min),
				absence: absence ? this.absenceBadgeOf(absence) : null,
				overrides: this.overrides.filter(o => o.id_employee === empId && o.dat === day.dat),
			};
		});
		// Les alertes légales regardent TOUTE la semaine de l'employé (tous EDT confondus) :
		// 6h de magasin + 5h de livraison le même jour doivent déclencher le > 10h.
		return {
			id_employee: empId,
			name: hrEmployeeNameOf(emp, this.users),
			cells,
			...this.weekFigures(empId, allEffective.filter(s => s.id_employee === empId), emp),
		};
	}

	private absenceBadgeOf(a: HrAbsence): AbsenceBadge {
		const typ = this.absenceTypes.find(t => t.id === a.typ);
		return {
			label: (typ?.name ?? 'Absence') + (a.state === HR_ABSENCE_STATE_REQUESTED ? ' (demandé)' : ''),
			color: typ?.data?.color ?? '#9e9e9e',
			pending: a.state === HR_ABSENCE_STATE_REQUESTED,
		};
	}

	// ============================================================
	// Affichage
	// ============================================================

	slotLabel(s: EffectiveSlot): string {
		return minutesLabel(s.start_min) + '–' + minutesLabel(s.end_min);
	}

	// Total de la semaine — sauf pour qui n'a pas de décompte : afficher « 35 h » à un gérant non
	// salarié ou à un cadre au forfait jours laisserait croire à un compteur qui n'existe pas.
	totalLabel(row: PlanningRow | EmployeeRow): string {
		return row.counts_hours === false ? '—' : hoursLabel(row.total_min);
	}

	// L'infobulle du tiret : sans elle, « — » passe pour une donnée manquante.
	totalTooltip(row: PlanningRow | EmployeeRow): string {
		return row.counts_hours === false
			? 'Temps non décompté pour cette personne : le planning l\'affiche, aucun compteur ne la suit.'
			: '';
	}

	// Utilisé par la vue Postes (sous-totaux de cellule et pied de colonne).
	hours(min: number): string {
		return hoursLabel(min);
	}

	// « +02h30 » / « −00h30 » — vide quand aucun contrat n'est renseigné ou que l'écart est nul.
	deltaLabel(row: PlanningRow | EmployeeRow): string {
		return deltaLabel(row.delta_min);
	}

	// 'over' = heures sup anticipées (rouge), 'under' = heures manquantes (bleu).
	deltaClass(row: PlanningRow | EmployeeRow): string {
		if (row.delta_min === null || row.delta_min === 0) return '';
		return row.delta_min > 0 ? 'over' : 'under';
	}

	// L'infobulle dit d'où vient le chiffre : les heures de congés payés comptent dans l'assiette,
	// et un temps partiel fait des heures COMPLÉMENTAIRES, pas des heures supplémentaires.
	deltaTooltip(row: PlanningRow | EmployeeRow): string {
		if (row.delta_min === null) return 'Aucun volume contractuel renseigné sur la fiche';
		const contract = hoursLabel(row.contract_min ?? 0);
		// On dit d'où vient le chiffre : « 3 j de congés × 7 h = 21 h », la base journalière étant
		// le contrat divisé par les jours travaillés de la semaine.
		const leave = row.assimilated_min > 0
			? '\n' + (row.assimilated_days > 0
				? (row.assimilated_days + ' j de congés × ' + hoursLabel(Math.round(row.assimilated_min / row.assimilated_days))
					+ ' = ' + hoursLabel(row.assimilated_min))
				: hoursLabel(row.assimilated_min) + ' de congés')
				+ ' comptés dans l\'assiette (Cass. soc. 10 sept. 2025)'
			: '';
		if (row.delta_min === 0) return 'Pile sur le contrat (' + contract + ')' + leave;
		if (row.delta_min < 0) {
			return hoursLabel(Math.abs(row.delta_min)) + ' sous le contrat (' + contract + ') — heures manquantes' + leave;
		}
		const kind = row.extra_kind === 'complementary' ? 'heures complémentaires' : 'heures supplémentaires';
		const beyond = row.extra_min > 0
			? '\n' + hoursLabel(row.extra_min) + ' au-delà du seuil légal ⇒ ' + kind
			: '\n(sous le seuil légal : pas encore d\'heures majorées)';
		return hoursLabel(row.delta_min) + ' au-dessus du contrat (' + contract + ')' + beyond + leave;
	}

	// Les jours d'absence de la semaine, en pastilles à côté des heures. « 3 j CP » explique d'un
	// coup d'œil pourquoi 16 h de travail suffisent à couvrir un contrat de 35 h.
	absenceChips(row: PlanningRow | EmployeeRow): { code: string; label: string; tooltip: string }[] {
		const chips: { code: string; label: string; tooltip: string }[] = [];
		const n = (d: number) => (d === Math.round(d) ? '' + d : d.toString().replace('.', ','));
		if (row.assimilated_days > 0) {
			chips.push({
				code: 'cp', label: n(row.assimilated_days) + ' j CP',
				tooltip: n(row.assimilated_days) + ' jour(s) de congés payés, valorisés '
					+ hoursLabel(row.assimilated_min) + ' dans l\'assiette (base journalière)',
			});
		}
		if (row.holiday_days > 0) {
			chips.push({
				code: 'holiday', label: n(row.holiday_days) + ' j férié',
				tooltip: n(row.holiday_days) + ' jour(s) férié(s) chômé(s) : ' + hoursLabel(row.holiday_min)
					+ ' maintenues, d\'après les horaires prévus ces jours-là (un férié chômé ne se'
					+ ' rattrape pas — L3133-1)',
			});
		}
		if (row.justified_days > 0) {
			chips.push({
				code: 'justified', label: n(row.justified_days) + ' j abs.',
				tooltip: n(row.justified_days) + ' jour(s) d\'absence justifiée comptée comme du travail',
			});
		}
		if (row.unjustified_days > 0) {
			chips.push({
				code: 'unjustified', label: n(row.unjustified_days) + ' j n.j.',
				tooltip: n(row.unjustified_days) + ' jour(s) d\'absence non assimilée (sans solde ou injustifiée) :'
					+ ' ces heures manquent réellement au compteur',
			});
		}
		return chips;
	}

	issuesTooltip(issues: LegalIssue[]): string {
		return issues.map(i => i.detail).join('\n');
	}

	// Repère de disponibilité dans le coin de la cellule : triangle VERT (disponible) ou
	// ROUGE (indisponible). Informatif — planifier reste possible.
	availabilityBadge(row: PlanningRow | EmployeeRow, dayIdx: number): { kind: number; tooltip: string } | null {
		return availabilityBadgeOf(this.availabilityMarks, row.id_employee, this.weekDays[dayIdx]?.dat ?? '');
	}

	// Un créneau posé sur une indisponibilité déclarée : on le signale, on ne l'interdit pas.
	availabilityConflicts(row: PlanningRow | EmployeeRow, dayIdx: number): string {
		const cell = row.cells[dayIdx];
		if (!cell?.slots.length) return '';
		const hits = cell.slots.flatMap(s =>
			conflictsWithUnavailability(this.availabilityMarks, row.id_employee, cell.dat, s.start_min, s.end_min));
		if (!hits.length) return '';
		return 'Planifié alors que la personne s\'est déclarée indisponible :\n'
			+ Array.from(new Set(hits.map(h => h.comment || 'indisponibilité déclarée'))).join('\n');
	}

	lockTooltip(day: { dat: string }): string {
		const store = this.storeCtrl.value;
		const lock = this.locks.find(l => HrLock.covers(l, day.dat, store));
		if (!lock) return '';
		const period = hrPeriodLabel(lock.datstart, lock.datend);
		return 'Période verrouillée pour la paie (' + period + ')'
			+ (lock.id_store == null ? ' — toute l\'organisation' : '')
			+ '\nPlus aucune modification de planning, d\'absence ni de pointage sur ce jour.';
	}

	// ============================================================
	// Publication (L6) — notifier UNIQUEMENT les salariés impactés
	// ============================================================

	onPublish(): void {
		if (!this.isHrManager) return;
		const store = this.storeCtrl.value;
		// Une publication porte sur UN établissement (hr_publication.id_store) : en « Tous », le
		// planning affiché n'en désigne aucun. On le dit — un bouton qui ne fait rien passe pour une panne.
		if (!store) {
			this.alertService.error('Choisissez un magasin dans la liste pour publier son planning');
			return;
		}

		const current = buildPublicationDigest(
			this.allEffective.filter(s => s.id_store === store));
		const previous = this.publications.length ? (this.publications[0].digest ?? {}) : null;
		const diff = publicationDiff(current, previous);

		const nameOf = (id: number) => {
			const emp = this.employees.find(e => e.id === id);
			return hrEmployeeNameOf(emp, this.users);
		};

		// On ne notifie jamais à l'aveugle : le gérant voit QUI sera prévenu avant de valider.
		Swal.fire({
			title: 'Publier le planning ?',
			text: publicationSummary(diff, nameOf),
			icon: diff.toNotify.length ? 'question' : 'info',
			showCancelButton: true,
			confirmButtonText: diff.toNotify.length ? 'Publier et notifier' : 'Publier quand même',
			cancelButtonText: 'Annuler',
		}).then(res => {
			if (!res.isConfirmed) return;
			const payload = {
				id: 0, id_store: store,
				datstart: this.monday, datend: addDays(this.monday, 6),
				digest: current,
				notified: diff.toNotify,
			};
			this.hrService.postHrEvent(EVENTTYP_INSERT, OBJECTTYP_HR_PUBLICATION, payload).subscribe({
				next: (r) => {
					// « En attente d'envoi » est exact mais laisse croire à un envoi imminent : AUCUN
					// transport e-mail/SMS n'est encore branché (cf. process_hr_publication_insert),
					// les lignes hr_notification patientent tant qu'il n'y en a pas. Le dire évite
					// de compter sur une notification qui ne partira pas et de ne prévenir personne.
					const queued = r?.data?.queued ?? 0;
					this.alertService.success(queued
						? 'Planning publié — ' + queued + ' salarié(s) concerné(s), à prévenir vous-même (l\'envoi automatique n\'est pas encore activé)'
						: 'Planning publié');
					this.loadWeek(this.date);
				},
				error: (e) => this.alertService.error(hrErrorMessage(e)),
			});
		});
	}

	// ============================================================
	// Impression (L3) — planning affiché ou feuille d'heures à signer
	// ============================================================

	onPrint(): void {
		Swal.fire({
			title: 'Imprimer',
			input: 'radio',
			inputOptions: {
				planning: 'Le planning de la semaine',
				timesheet: 'La feuille d\'heures à faire signer',
				schedule: 'Les horaires types (grilles de roulement)',
			},
			inputValue: 'planning',
			showCancelButton: true,
			confirmButtonText: 'Imprimer',
			cancelButtonText: 'Annuler',
		}).then(res => {
			if (!res.isConfirmed || !res.value) return;
			// Les trois documents proposés par CE dialogue — le relevé individuel (kind 'ledger'),
			// lui, s'imprime depuis l'écran des compteurs (/hr/timesheets).
			const kind = res.value as 'planning' | 'timesheet' | 'schedule';
			const doc = kind === 'schedule' ? this.buildSchedulesDoc() : this.buildPrintDoc(kind);
			if (!doc) {
				this.alertService.error('Aucun horaire type actif à imprimer pour cette sélection.');
				return;
			}
			// Le document est rendu par le SERVEUR (POST /hr/print) : la fenêtre s'ouvre ici, dans le
			// prolongement du clic, et reçoit le HTML dès qu'il arrive.
			this.printService.print(doc).subscribe({
				error: (e) => this.alertService.error(hrPrintErrorMessage(e)),
			});
		});
	}

	// L'en-tête du papier doit dire de QUOI il parle : un magasin, ou toute l'organisation.
	private printStoreName(): string {
		return this.storeCtrl.value == null
			? 'Tous les magasins'
			: this.stores.find(s => s.id === this.storeCtrl.value)?.name ?? '';
	}

	// Ce que le composant DONNE au module d'impression : l'état affiché, plus les libellés que lui
	// seul sait produire (couleurs choisies à la main, noms de lieux, pastilles d'absence).
	// L'assemblage du document, lui, est pur et testé — cf. hr-planning-print.ts.
	private buildPrintDoc(kind: 'planning' | 'timesheet'): PrintDoc {
		return buildPlanningPrintDoc({
			kind,
			byPoste: this.viewMode === 'postes',
			dayIdx: this.visibleDayIndices,
			weekDays: this.weekDays,
			weekLabel: this.weekLabel,
			storeName: this.printStoreName(),
			printedAt: this.fmtLong(todayStr()),
			employeeRows: this.employeeRows,
			posteRows: this.posteRows,
			resolvers: {
				employeeColor: (id) => this.employeeColor(id),
				slotColor: (s) => this.slotColor(s),
				placeLabel: (s) => this.placeLabel(s),
				absenceChips: (row) => this.absenceChips(row as EmployeeRow).map(c => c.label),
				fullDayAbsence: (cell) => this.fullDayAbsence(cell as EmployeeCell),
				posteDayTotals: (i) => this.dayTotals(i),
			},
		});
	}

	private buildSchedulesDoc(): PrintDoc | null {
		return buildSchedulesPrintDoc({
			schedules: this.schedules,
			activities: this.activities,
			employees: this.employees,
			users: this.users,
			rules: this.rules,
			store: this.storeCtrl.value,
			storeName: this.printStoreName(),
			storeNames: new Map(this.stores.map(s => [s.id ?? -1, s.name ?? ''])),
			printedAt: this.fmtLong(todayStr()),
			today: todayStr(),
			employeeColor: (id) => this.employeeColor(id),
			mergeRanges: (ranges) => mergeRanges(ranges),
		});
	}

	private employeeNameOf(id: number): string {
		return hrEmployeeNameOf(this.employees.find(e => e.id === id), this.users);
	}

	private fmtLong(dat: string): string {
		return dat.slice(8, 10) + '/' + dat.slice(5, 7) + '/' + dat.slice(0, 4);
	}

	// Note de journée : une par magasin × jour. Vider le texte SUPPRIME la note.
	onEditNote(day: { dat: string; note: string }): void {
		if (!this.isHrManager) return;
		const store = this.storeCtrl.value;
		// La note appartient à un établissement (hr_note.id_store) : en « Tous » on les LIT toutes,
		// mais en écrire une demande de désigner lequel.
		if (!store) {
			this.alertService.error('Choisissez un magasin dans la liste pour annoter la journée');
			return;
		}
		const existing = this.notes.find(n => n.dat === day.dat && n.id_store === store);
		Swal.fire({
			title: 'Note du ' + this.fmtShort(day.dat),
			input: 'textarea',
			inputValue: existing?.txt ?? '',
			inputPlaceholder: 'Livraison exceptionnelle, inventaire, réunion…',
			showCancelButton: true,
			confirmButtonText: 'Enregistrer',
			cancelButtonText: 'Annuler',
		}).then(res => {
			if (!res.isConfirmed) return;
			const txt = (res.value ?? '').trim();

			// Texte vidé sur une note existante = suppression ; texte vide sans note = rien à faire.
			if (!txt) {
				if (!existing) return;
				this.hrService.postHrEvent(EVENTTYP_DELETE, OBJECTTYP_HR_NOTE, { id: existing.id }).subscribe({
					next: () => { this.alertService.success('Note supprimée'); this.loadWeek(this.date); },
					error: (e) => this.alertService.error(hrErrorMessage(e)),
				});
				return;
			}

			const payload = this.hrService.buildNotePayload({ id: existing?.id ?? 0, id_store: store, dat: day.dat, txt });
			const typ = existing ? EVENTTYP_UPDATE : EVENTTYP_INSERT;
			this.hrService.postHrEvent(typ, OBJECTTYP_HR_NOTE, payload).subscribe({
				next: () => { this.alertService.success('Note enregistrée'); this.loadWeek(this.date); },
				error: (e) => this.alertService.error(hrErrorMessage(e)),
			});
		});
	}

	// ============================================================
	// Clôture d'une journée — l'ajustement de fin de journée du mode planning
	// ============================================================
	//
	// Le planning a annoncé la journée ; la clôture dit ce qui s'est réellement passé et FIGE les
	// heures qui partent en paie (`HrDayValidation`). Sans elle, le rapport paierait le prévu même
	// quand quelqu'un est parti deux heures plus tôt.
	//
	// Pointages et validations sont chargés à l'ouverture, pas au chargement de la semaine : c'est
	// un geste occasionnel, inutile d'alourdir chaque navigation. Ils sont aussi plus frais ainsi.

	// Une journée ne se clôture qu'une fois passée : figer un jour à venir écrirait des heures
	// « réalisées » qui n'ont pas eu lieu, et le rapport cesserait de suivre le planning.
	// Et c'est un arbitrage de paie : responsable RH uniquement.
	canCloseDay(day: { dat: string }): boolean {
		return this.isHrManager && day.dat <= todayStr();
	}

	onCloseDay(day: { dat: string; label: string; locked: boolean }): void {
		if (!this.canCloseDay(day)) return;
		if (day.locked) {
			this.alertService.error('Journée verrouillée pour la paie : déverrouillez la période d\'abord');
			return;
		}
		const store = this.storeCtrl.value;
		const employees = this.employeeRows.map(r => ({ id: r.id_employee, name: r.name }));
		if (!employees.length) {
			this.alertService.error('Aucun salarié affiché sur cette semaine');
			return;
		}

		forkJoin({
			clocks: this.hrService.getClocks$(day.dat, day.dat),
			validations: this.hrService.getValidations$(day.dat, day.dat),
		}).subscribe(({ clocks, validations }) => {
			const data: DayCloseDialogData = {
				dat: day.dat,
				dayLabel: day.label,
				storeName: store == null ? 'Tous les magasins' : this.stores.find(s => s.id === store)?.name ?? '',
				employees,
				slots: this.allEffective.filter(s => s.dat === day.dat),
				periods: pairClocks(clocks, todayStr()).periods,
				validations,
				source: this.source,
			};
			this.dialog.open(DayCloseDialogComponent, { data, width: '820px', maxWidth: '95vw' })
				.afterClosed().subscribe((changed: DayCloseRow[] | undefined) => {
					if (!changed || !changed.length) return;
					this.postDayClose(changed, day.dat);
				});
		});
	}

	// Un event par salarié modifié ; on recharge une fois toutes les réponses reçues.
	private postDayClose(rows: DayCloseRow[], dat: string): void {
		let remaining = rows.length;
		let failed = false;
		for (const r of rows) {
			const payload = this.hrService.buildValidationPayload({
				id: r.id, id_employee: r.id_employee, dat,
				planned_min: r.planned_min, badged_min: r.badged_min,
				paid_min: r.paid_min, break_min: r.break_min,
				absent: r.absent, validated: r.validated, comment: r.comment,
			});
			this.hrService.postHrEvent(EVENTTYP_INSERT, OBJECTTYP_HR_DAY_VALIDATION, payload).subscribe({
				next: () => {
					if (--remaining === 0) {
						if (!failed) this.alertService.success('Journée clôturée — ' + rows.length + ' salarié(s)');
						this.loadWeek(this.date);
					}
				},
				// Refus métier (période verrouillée… — postHrEvent le convertit en erreur RxJS)
				// comme vraie panne.
				error: (e) => {
					failed = true;
					if (--remaining === 0) this.loadWeek(this.date);
					this.alertService.error(hrErrorMessage(e));
				},
			});
		}
	}

	cellIssues(row: PlanningRow | EmployeeRow, dayIdx: number): LegalIssue[] {
		return row.issues.filter(i => i.weekday === dayIdx + 1);
	}

	weekIssues(row: PlanningRow | EmployeeRow): LegalIssue[] {
		return row.issues.filter(i => i.weekday === null);
	}

	// ============================================================
	// Navigation de semaine (URL = source de vérité)
	// ============================================================

	goToDate(dat: string): void {
		this.router.navigate(['/hr/planning', dat]);
	}

	onPrevWeek(): void { this.goToDate(addDays(this.monday, -7)); }
	onNextWeek(): void { this.goToDate(addDays(this.monday, 7)); }
	onToday(): void { this.goToDate(todayStr()); }

	// ============================================================
	// Exceptions (dialog) — chaque action est persistée immédiatement
	// ============================================================

	onCellClick(section: ScheduleSection, row: PlanningRow, dayIdx: number): void {
		if (!this.isHrManager) return;			// consultation seule
		const cell = row.cells[dayIdx];
		const data: OverrideDialogData = {
			employeeName: row.name,
			id_employee: row.id_employee,
			dat: cell.dat,
			dayLabel: this.weekDays[dayIdx].label + (this.weekDays[dayIdx].holiday ? ' (' + this.weekDays[dayIdx].holiday + ')' : ''),
			scheduleName: section.schedule.name,
			theoreticalSlots: cell.slots.filter(s => s.origin === 'cycle')
				.map(s => ({ start_min: s.start_min, end_min: s.end_min, place: this.placeLabelOfSchedule(s.id_schedule) })),
			overrides: cell.overrides,
			// La section dit déjà le service, mais la personne peut être mobile : le lieu reste un choix.
			places: this.placesFor(row.id_employee, cell.dat),
			id_schedule: section.schedule.id,
		};
		this.openOverrideDialog(data, section.schedule.id);
	}

	private openOverrideDialog(data: OverrideDialogData, scheduleId: number): void {
		this.dialog.open(OverrideDialogComponent, { data, width: '480px' }).afterClosed().subscribe((result: OverrideDialogResult) => {
			if (!result) return;
			if (result.kind === 'remove-day') {
				const o = Object.assign(new HrOverride(), { id: 0, id_employee: result.id_employee, dat: result.dat, typ: HR_OVERRIDE_REMOVE, comment: result.comment });
				this.postOverride(EVENTTYP_INSERT, o);
			} else if (result.kind === 'add') {
				const o = Object.assign(new HrOverride(), {
					id: 0, id_employee: result.id_employee, dat: result.dat, typ: HR_OVERRIDE_ADD,
					// Le LIEU choisi dans le dialogue fait foi ; `scheduleId` n'est plus qu'un repli
					// pour un appelant qui n'aurait pas proposé de liste.
					id_schedule: result.id_schedule || scheduleId,
					start_min: result.start_min, end_min: result.end_min, comment: result.comment,
				});
				this.postOverride(EVENTTYP_INSERT, o);
			} else if (result.kind === 'delete-override') {
				const o = Object.assign(new HrOverride(), { id: result.id });
				this.postOverride(EVENTTYP_DELETE, o);
			}
		});
	}

	private postOverride(typ: number, o: HrOverride): void {
		this.hrService.postHrEvent(typ, OBJECTTYP_HR_OVERRIDE, this.hrService.buildOverridePayload(o)).subscribe({
			next: () => this.loadWeek(this.date),
			error: (e) => this.alertService.error(hrErrorMessage(e)),
		});
	}
}
